/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { combineReducers, configureStore } from '@reduxjs/toolkit';
import createSagaMiddleware from 'redux-saga';
import customMiddleware from 'modules/common/middleware/custom-middleware';
import { authSlice } from 'modules/common/auth/slice';
import { signInSlice } from 'modules/sign-in/slice';
import { createTransform, persistReducer, persistStore } from 'redux-persist';
import hardSet from 'redux-persist/lib/stateReconciler/hardSet';
import storage from 'redux-persist/lib/storage';
import { ENVIRONMENT } from 'config';
import { forgotPasswordSlice } from 'modules/forgot-password/slice';
import { resetPasswordSlice } from 'modules/reset-password/slice';
import { notificationSlice } from 'modules/common/notifications/slice';
import { verifyCallbackSlice } from 'modules/verify-callback/slice';
import { dashboardSlice } from 'modules/dashboard/slice';
import { rateCalenderSlice } from 'modules/rate-calendar/slice';
import logger from 'modules/common/utils/logger';
import { rateCalendarUploadSlice } from 'modules/rate-calendar-upload/slice';
import { usersSlice } from 'modules/users/slice';
import { configureEventCategoriesSlice } from 'modules/event-configuration/slice';
import { summarySlice } from 'modules/dashboard/components/tab-container/summary-tab/slice';
import { trendSlice } from 'modules/dashboard/components/tab-container/trend-tabs/slice';
import { regionalMapSlice } from 'modules/dashboard/components/tab-container/regional-map-tab/slice';
import { SLICE } from 'modules/dashboard/constants';
import { webClickSlice } from 'modules/dashboard/components/tab-container/web-click-tab/slice';
import { iPreferSlice } from 'modules/dashboard/components/tab-container/i-prefer-tab/slice';
import { workbookSlice } from 'modules/dashboard/components/tab-container/workbook-report/slice';
import rootSaga from 'root-saga';
import { injectStore } from 'store/accessor';
import { paceSlice } from 'modules/dashboard/components/tab-container/pace-tabs/slice';
import { trendTabularSlice } from 'modules/dashboard/components/tab-container/trend-tabular-tabs/slice';
import { alertSlice } from 'modules/dashboard/components/tab-container/alert-tab/slice';
import { searchSlice } from 'modules/dashboard/components/tab-container/search/slice';
import { customChatSlice } from 'modules/common/components/custom-chat-view/slice';
//
export const sagaMiddleware = createSagaMiddleware();
/**
 * Command url state of the dashboard slice is only valid while a command url is being applied
 * and must not be restored on reload
 */
const resetCommandUrlState = (state) => ({ ...state, commandRun: null, urlTab: null });
const dashboardCommandTransform = createTransform(resetCommandUrlState, resetCommandUrlState, {
  whitelist: [SLICE.DASHBOARD],
});
/**
 * White listed slices which needs to be persist with encryption
 */
const persistConfig = {
  version: ENVIRONMENT.PERSIST_VERSION,
  key: ENVIRONMENT.PERSIST_KEY,
  storage,
  stateReconciler: hardSet,
  transforms: [dashboardCommandTransform],
  whitelist: [
    SLICE.BASE_AUTH,
    SLICE.DASHBOARD,
    SLICE.SUMMARY,
    SLICE.PACE_TIME,
    SLICE.PACE_SEGMENT,
    SLICE.REGIONAL_MAP,
    SLICE.WEB_CLICK,
    SLICE.I_PREFER,
    SLICE.WORKBOOK_REPORT,
    SLICE.TREND,
    SLICE.TREND_TABULAR,
    SLICE.PACE,
    SLICE.ALERT,
    SLICE.SEARCH,
    SLICE.CUSTOM_CHAT
  ],
  writeFailHandler(error) {
    logger.error(error);
  },
};
/**
 * Alert slice persist config - remarks and excel are transient API responses
 * and must not be restored on reload
 */
const alertPersistConfig = {
  key: SLICE.ALERT,
  storage,
  blacklist: ['alertRemarks', 'alertExcel'],
};
/**
 * Combine all the persist and non persist reducers
 */
const reducers = combineReducers({
  [SLICE.BASE_AUTH]: authSlice.reducer,
  [SLICE.SIGN_IN]: signInSlice.reducer,
  [SLICE.FORGOT_PASSWORD]: forgotPasswordSlice.reducer,
  [SLICE.RESET_PASSWORD]: resetPasswordSlice.reducer,
  [SLICE.NOTIFICATION]: notificationSlice.reducer,
  [SLICE.VERIFY_CALLBACK]: verifyCallbackSlice.reducer,
  [SLICE.DASHBOARD]: dashboardSlice.reducer,
  [SLICE.RATE_CALENDAR_UPLOAD]: rateCalendarUploadSlice.reducer,
  [SLICE.USERS]: usersSlice.reducer,
  [SLICE.RATE_CALENDAR]: rateCalenderSlice.reducer,
  [SLICE.CONFIGURE_EVENT_CATEGORIES]: configureEventCategoriesSlice.reducer,
  [SLICE.SUMMARY]: summarySlice.reducer,
  [SLICE.REGIONAL_MAP]: regionalMapSlice.reducer,
  [SLICE.WEB_CLICK]: webClickSlice.reducer,
  [SLICE.I_PREFER]: iPreferSlice.reducer,
  [SLICE.WORKBOOK_REPORT]: workbookSlice.reducer,
  [SLICE.TREND]: trendSlice.reducer,
  [SLICE.TREND_TABULAR]: trendTabularSlice.reducer,
  [SLICE.PACE]: paceSlice.reducer,
  [SLICE.ALERT]: persistReducer(alertPersistConfig, alertSlice.reducer),
  [SLICE.SEARCH]: searchSlice.reducer,
  [SLICE.CUSTOM_CHAT]: customChatSlice.reducer,
});
/**
 * Define what are the slice which needs to be persist
 */
const persistedReducer = persistReducer(persistConfig, reducers);
/**
 * Register all the slices into the main store with encryption
 */
const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) => [
    ...getDefaultMiddleware({
      serializableCheck: { ignoredActions: ['persist/PERSIST'] },
    }),
    sagaMiddleware,
    customMiddleware,
  ],
});
injectStore(store);
sagaMiddleware.run(rootSaga);
persistStore(store);
//
export default store;
